import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Facebook, Instagram, Linkedin } from 'lucide-react';
import { homeContent } from '../data/mockData';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import SEO from '../components/SEO';

const Home = () => {
  const [newsletterSubmitted, setNewsletterSubmitted] = useState(false);
  
  // Scroll animation hooks for different sections
  const [heroRef, heroVisible] = useScrollAnimation({ threshold: 0.1 });
  const [coreValuesRef, coreValuesVisible] = useScrollAnimation({ threshold: 0.1 });
  const [actionRef, actionVisible] = useScrollAnimation({ threshold: 0.1 });
  const [newsletterRef, newsletterVisible] = useScrollAnimation({ threshold: 0.1 });
  
  // Structured Data for homepage
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Person",
    "name": "Maryka Groenewald",
    "jobTitle": "Member of Legislative Council",
    "affiliation": {
      "@type": "Organization",
      "name": "Australian Christians"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Perth",
      "addressRegion": "WA",
      "addressCountry": "AU"
    },
    "url": "https://www.marykagroenewald.com",
    "sameAs": [
      "https://www.facebook.com/MarykaGroenewaldAC",
      "https://www.instagram.com/maryka.groenewald.mlcforwa/",
      "https://www.linkedin.com/in/marykagroenewald/"
    ]
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const data = {
      name: formData.get('name'),
      email: formData.get('email')
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setNewsletterSubmitted(true);
        e.target.reset();
        setTimeout(() => setNewsletterSubmitted(false), 5000);
      } else {
        alert('Failed to sign up. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter signup error:', error);
      alert('Failed to sign up. Please try again.');
    }
  };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Hon Maryka Groenewald MLC | Australian Christians WA"
        description="Maryka Groenewald, Australian Christians Member of Legislative Council for Western Australia. Fighting for life, faith, family and freedom."
        keywords="Maryka Groenewald, Australian Christians, WA Parliament, Legislative Council, Western Australia, pro-life, religious freedom, family values"
        canonical="/"
        structuredData={structuredData}
      />
      
      {/* Hero Section */}
      <section ref={heroRef} className="pt-32 pb-20 px-6 lg:px-12 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className={`text-center lg:text-left ${heroVisible ? 'animate-fade-in-left' : 'animate-on-scroll'}`}>
              <span className="text-gold text-xl md:text-2xl font-bold tracking-widest uppercase block mb-4">
                {homeContent.hero.tagline}
              </span>
              <h1 className="mt-6 font-cormorant text-5xl md:text-7xl lg:text-8xl text-navy leading-tight hover-text-zoom">
                <span className="block md:inline">{homeContent.hero.mainText}</span>
                <br className="hidden md:block" />
                <span className="text-gold italic font-cormorant-garamond font-bold block">
                  {homeContent.hero.stoked}
                </span>
                <span className="block">{homeContent.hero.endText}</span>
              </h1>
              <p className="mt-8 text-lg text-gray-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
                {homeContent.hero.description}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <Button
                  asChild
                  className="bg-gold hover:bg-[#e09f0a] text-white font-bold px-8 py-5 md:py-6 text-sm tracking-wide rounded-md transition-colors w-full sm:w-auto"
                >
                  <Link to="/about">{homeContent.hero.buttons[0].text}</Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="border-2 border-navy text-navy hover:bg-navy hover:text-white font-bold px-8 py-5 md:py-6 text-sm tracking-wide rounded-md transition-colors w-full sm:w-auto"
                >
                  <Link to="/contact">{homeContent.hero.buttons[1].text}</Link>
                </Button>
              </div>
            </div>

            {/* Right Image */}
            <div className={`relative max-w-md mx-auto lg:max-w-lg ${heroVisible ? 'animate-fade-in-right delay-200' : 'animate-on-scroll'}`}>
              <div className="rounded-2xl overflow-hidden shadow-2xl hover-zoom">
                <img
                  src={homeContent.hero.image}
                  alt="Maryka Groenewald"
                  className="w-full h-auto object-cover transition-transform duration-300"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section ref={coreValuesRef} className="py-20 px-6 lg:px-12 bg-cream">
        <div className="container mx-auto text-center">
          <span className={`text-gold text-xl md:text-2xl font-bold tracking-widest uppercase block mb-4 ${coreValuesVisible ? 'animate-float-in' : 'animate-on-scroll'}`}>
            {homeContent.coreValues.tagline}
          </span>
          <h2 className={`mt-6 font-cormorant text-4xl md:text-6xl lg:text-7xl text-navy italic hover-text-zoom ${coreValuesVisible ? 'animate-fade-in-up delay-100' : 'animate-on-scroll'}`}>
            {homeContent.coreValues.title}
          </h2>

          <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12">
            {homeContent.coreValues.values.map((value, index) => (
              <div
                key={index}
                className={`flex flex-col items-center group cursor-pointer ${coreValuesVisible ? `animate-zoom-in delay-${(index + 2) * 100}` : 'animate-on-scroll'}`}
              >
                <div className={`w-72 h-72 md:w-48 md:h-48 flex items-center justify-center mb-6 icon-hover-subtle ${coreValuesVisible ? 'in-view' : ''}`}>
                  <img
                    src={value.icon}
                    alt={value.title}
                    className="w-full h-full object-contain filter-gold"
                  />
                </div>
                <h3 className="font-cormorant-garamond text-4xl md:text-4xl italic text-navy mb-3 hover-text-zoom">
                  {value.title}
                </h3>
                <p className="text-base md:text-lg text-gray-600 max-w-xs text-center leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* You Have a Part to Play Section */}
      <section ref={actionRef} className="py-20 px-6 lg:px-12 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            {/* Image */}
            <div className={`hover-zoom max-w-md mx-auto lg:max-w-lg ${actionVisible ? 'animate-fade-in-left' : 'animate-on-scroll'}`}>
              <div className="rounded-2xl overflow-hidden shadow-xl">
                <img
                  src="/assets/home - I was elected.jpg"
                  alt="Community volunteers"
                  className="w-full h-auto object-cover transition-transform duration-300"
                />
              </div>
            </div>

            {/* Content */}
            <div className={`${actionVisible ? 'animate-fade-in-right delay-200' : 'animate-on-scroll'}`}>
              <span className="text-navy text-xl md:text-2xl font-bold tracking-widest uppercase">
                YOU HAVE A PART TO PLAY
              </span>
              <h2 className="mt-4 font-cormorant-garamond text-4xl md:text-5xl text-navy italic hover-text-zoom">
                Let's encourage and strengthen WA!
              </h2>
              <div className="mt-6 text-gray-600 leading-relaxed text-lg space-y-4">
                <p>Good things happen when people get involved in shaping laws that govern us.</p>
                <p>If you share our mission for a thriving WA, we'd love your help.</p>
                <p>Join my email list, sign up to volunteer or join events, share our updates, or back our petitions.</p>
                <p>Every step builds a State that protects life, honours family, respects faith and defends freedom.</p>
              </div>
              <Button
                asChild
                className="mt-8 bg-gold hover:bg-[#e09f0a] text-white font-bold px-8 py-6 text-sm tracking-wide rounded-md transition-colors"
              >
                <Link to="/contact">FIND OUT MORE</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Signup Section - Gold Background */}
      <section ref={newsletterRef} className="py-20 px-6 lg:px-12 bg-gold">
        <div className="container mx-auto text-center max-w-2xl">
          <span className={`text-navy text-xl md:text-2xl font-bold tracking-widest uppercase mb-4 block ${newsletterVisible ? 'animate-float-in' : 'animate-on-scroll'}`}>
            SIGN UP
          </span>
          <h2 className={`font-cormorant-garamond text-4xl md:text-5xl lg:text-6xl text-navy italic hover-text-zoom mb-4 ${newsletterVisible ? 'animate-fade-in-up delay-100' : 'animate-on-scroll'}`}>
            Stay in the loop
          </h2>
          <p className={`text-navy/80 text-lg leading-relaxed mb-10 ${newsletterVisible ? 'animate-fade-in-up delay-200' : 'animate-on-scroll'}`}>
            Get the latest on Parliament, campaigns, and my work for you as an Australian Christians MLC, delivered direct to your inbox.
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className={`space-y-4 max-w-md mx-auto ${newsletterVisible ? 'animate-zoom-in delay-300' : 'animate-on-scroll'}`}>
            <input
              type="text"
              name="name"
              placeholder="NAME"
              required
              className="w-full px-4 md:px-6 py-3 md:py-4 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-navy uppercase text-sm"
            />
            <input
              type="email"
              name="email"
              placeholder="EMAIL"
              required
              className="w-full px-4 md:px-6 py-3 md:py-4 rounded-full text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-navy uppercase text-sm"
            />
            <button
              type="submit"
              className="bg-navy hover:bg-navy/90 text-white font-bold px-8 md:px-12 py-3 md:py-4 rounded-full text-sm tracking-wide transition-colors uppercase w-full sm:w-auto"
            >
              SIGN UP
            </button>
            {newsletterSubmitted && (
              <p className="text-navy font-medium mt-4 text-lg">Thank you for signing up!</p>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

export default Home;
